/**
 * /zh/comparisons/chatprd — ChatPrd.tsx 的中文孪生页。结构与英文版
 * 一一对应；诚实约定相同：ChatPRD 在「从零到初稿」和 CPO 式教练上
 * 确实强，直接说；Draftlize 的楔子是初稿之后——PRD 变成卡片图谱，
 * 决策一改依赖全部标 stale，agent 通过 MCP 读活的版本。
 */
import { MarketingShell } from '../MarketingShell';

export function ChatPrdZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/comparisons/chatprd"
      documentTitle="ChatPRD 替代品？Draftlize vs ChatPRD：初稿之后才是分水岭 · Draftlize"
      documentDescription="ChatPRD 能极快地生成一份漂亮的 PRD——这份诚实对比讲的是之后的事。在 Draftlize 里 PRD 是卡片图谱：改一个决策，依赖它的全部自动标 stale，你的编码 agent 通过 MCP 读到的永远是活的版本，不是复制件。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">对比 · vs ChatPRD</span>
          <h1
            className="serif"
            style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}
          >
            Draftlize vs ChatPRD：<br/>
            <em>比的不是初稿，是初稿之后。</em>
          </h1>
          <p className="lede">
            一句话结论：从一行想法到一份体面的 <a href="/zh/product-requirements-document" style={{ color: 'var(--amber)' }}>PRD</a>，ChatPRD 是最快的，而且它的 CPO 式教练反馈是真的好。Draftlize 从那份初稿结束的地方开始：PRD 变成一张可寻址的决策卡片图谱——上游一个决策变了，依赖它的每一处自动标 stale；你的编码 agent 通过 MCP 读到的是当前版本，而不是某次导出的复制件。一个优化「写」，一个负责「写完之后还成立」。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">并排看，<em>7 个维度。</em></h2>
          </div>
          <p>重心不同：ChatPRD 是带教练的文档生成器；Draftlize 是带生成的决策基座。下面这些行就是差异咬人的地方。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>ChatPRD</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>AI 产出的东西</td><td className="ours yes">卡片图谱——每个决策可寻址</td><td className="partial">一份完整的 PRD 文档</td></tr>
            <tr><td>从零到初稿的速度</td><td className="partial">快，但产出是结构化卡片</td><td className="yes">极快——它的主场</td></tr>
            <tr><td>CPO 式教练反馈</td><td className="no">—（以图谱缺口检测代替）</td><td className="yes">有，而且不错</td></tr>
            <tr><td>初稿之后决策变了</td><td className="ours yes">依赖卡片自动标 stale</td><td className="no">重新生成；同步出去的副本悄悄失真</td></tr>
            <tr><td>编码 agent 读什么</td><td className="ours yes">每一轮通过 MCP 读活卡片</td><td className="partial">导出到 Notion / Linear / v0 的文档</td></tr>
            <tr><td>PRD 与实现之间的漂移</td><td className="ours yes">自动翻成 drift_detected</td><td className="no">不可见，直到 review 撞上</td></tr>
            <tr><td>团队工作区集成</td><td className="partial">Claude Code / Cursor / MCP + CLI，agent 优先</td><td className="yes">12+：Linear、Notion、Slack、GitHub、Confluence</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif"><em>ChatPRD 赢</em>在哪。</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">品类里最快的初稿</h3>
            <p>输入一行，出来一份结构完整、像模像样的 PRD。如果你的瓶颈是白纸时间——PRD 写得多、每份都从零开始——ChatPRD 配得上它「PM 第一 AI 平台」的自我定位。在这件事上这里没有谁能赢它。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">不止生成，还有教练</h3>
            <p>ChatPRD 的 CPO 级反馈会戳你写的东西里的战略缺口和没说出口的假设。如果你是早期 PM，或者是公司里唯一的 PM，这个常驻的批评伙伴是 Draftlize 不提供的真实价值。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">更成熟的团队工作流</h3>
            <p>共享空间、自定义 persona、企业权限，再加上通向 Linear、Notion、Slack、GitHub、Confluence 以及 v0、Lovable 这类 AI 建站工具的集成。如果 PRD 今天就必须落进现有团队栈，ChatPRD 的管线铺得更远。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif"><em>Draftlize 赢</em>在哪。</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">第一次决策变更后，PRD 还活着</h3>
            <p>生成出来的文档只在生成那一刻是对的。在 Draftlize 里，PRD 是接在<a href="/zh/decision-log" style={{ color: 'var(--amber)' }}>决策</a>上的卡片：上游翻转一个决策，依赖它的卡片自己标 <code>stale</code>——像构建系统作废下游产物一样。不用重读六页纸去找这次改动碰了什么。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">agent 读的是基座，不是粘贴件</h3>
            <p>ChatPRD 把成品文档交给你的工具，之后 agent 面对的都是副本。Draftlize 是 agent 直接工作于其中的基座：Claude Code 或 Cursor 写代码前先通过 MCP 拉当前卡片，实现跟着「现在的 PRD」走，而不是「上次导出时的 PRD」。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">一个决策，处处引用</h3>
            <p>定价决策只活在一张卡片里，被 PRD、<a href="/zh/product-spec-template" style={{ color: 'var(--amber)' }}>产品 spec</a>、发布清单共同引用。之后哪个线程里说了矛盾的话，冲突会浮出来。而在文档世界里，同一个决策存在 N 份措辞不同的转述，各漂各的。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          ChatPRD 回答的是「周五之前怎么写出一份好 PRD」。Draftlize 回答的是「到了三月，这份 PRD 凭什么还是真的」。
        </blockquote>
        <cite>写不出来是痛，选 ChatPRD；写完就漂是痛，那是 Draftlize。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">常见问题。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'Draftlize 算 ChatPRD 的替代品吗？', a: '就「AI 帮我产出一份 PRD」这件事来说，算——两个都能做。但它们优化的是两个半场：ChatPRD 优化初稿速度和教练质量；Draftlize 优化初稿之后——决策变更、文档悄悄失真的那一段。如果你只要最快拿到一份漂亮文档，选 ChatPRD 更合适。' },
            { q: 'ChatPRD 和 Draftlize 真正的区别是什么？', a: '产出的形态。ChatPRD 产出一份导出到你工作栈里的文档；Draftlize 产出一张卡片图谱：PRD 里每个决策都是一张声明了依赖关系的可寻址卡片，改动会传播——依赖标 stale、矛盾浮出、agent 通过 MCP 读活图谱而不是副本。' },
            { q: '可以同时用 ChatPRD 和 Draftlize 吗？', a: '可以，而且接缝很干净：喜欢 ChatPRD 的速度和教练就用它起草，然后把可长期沿用的决策落成 Draftlize 卡片。文档继续当人类看的快照；卡片成为 agent 读取、负责追踪漂移的事实源。' },
            { q: 'Draftlize 自己也能用 AI 生成 PRD 吗？', a: '能——在 Draftlize 里聊着聊着就把 PRD 聊出来了。区别在结果：不是一篇长文档，而是一组结构化卡片（问题、目标、决策、需求），每张单独可寻址、单独可引用，从第一天起就接在 stale 传播机制上。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">注册即送 <em>$5，</em>免费开始。</h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>用多少付多少</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            起草工具随你留着。Draftlize 是决策之后长期居住的地方——看看 <a href="/zh/ai-prd" style={{ color: 'var(--amber)' }}>AI PRD 工作台</a>，或者<a href="/zh/prd-template" style={{ color: 'var(--amber)' }}>不会漂移的 PRD 模板</a>。
          </p>
          <a className="btn-primary" href="/">注册领 $5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
